import React, { useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Receipt as FiReceipt,
  ChevronDown as FiChevronDown,
  ChevronUp as FiChevronUp,
} from 'react-bootstrap-icons';
import styles from './FacturacionGuideCard.module.scss';

export type FacturacionGuideVariant = 'orders' | 'integrations';

export interface FacturacionGuideCardProps {
  variant?: FacturacionGuideVariant;
  /** Profit Plus enabled and reference setup complete enough for app features */
  profitPlusAppReady: boolean;
  /** Accepted sales export to Profit as pedidos (export_only or bidirectional + toggle) */
  profitExportPedidosActive: boolean;
  /** Opens Profit Plus invoicing GIF drawer (Ventas screen only) */
  onOpenProfitInvoiceHelp?: () => void;
  /** Extra classes on the outer wrapper */
  className?: string;
  /** Soft tinted panel (Ventas) vs nested tertiary panel (Profit Plus card body) */
  visualTone?: 'soft' | 'embedded';
  /** When false, only title + subtitle show until the user expands */
  initiallyExpanded?: boolean;
  /** Short header copy for integrations (e.g. settings card). */
  minimalLead?: boolean;
}

/**
 * Explains how “facturación” works in MayorX vs Profit Plus 2k8 (legal invoice).
 */
const FacturacionGuideCard: React.FC<FacturacionGuideCardProps> = ({
  variant = 'orders',
  profitPlusAppReady,
  profitExportPedidosActive,
  onOpenProfitInvoiceHelp,
  className = '',
  visualTone = 'soft',
  initiallyExpanded = false,
  minimalLead = false,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);

  const title =
    minimalLead && variant === 'integrations'
      ? 'Guía: facturación y Profit'
      : variant === 'integrations'
        ? 'Facturación fiscal y Profit Plus 2k8'
        : 'Cómo facturar una venta';

  const subtitle =
    minimalLead && variant === 'integrations'
      ? 'Detalles al expandir.'
      : variant === 'integrations'
        ? 'MayorX registra ventas y, con Profit Plus activo, envía cada venta aprobada como pedido; la factura fiscal la crea usted desde Profit Plus.'
        : profitPlusAppReady && profitExportPedidosActive
        ? 'Por el momento MayorX sincroniza la venta aprobada como pedido en Profit Plus 2k8; desde Profit Plus podrá crear la factura. La nota de entrega y «marcar como facturado» en MayorX son pasos distintos.'
        : profitPlusAppReady && !profitExportPedidosActive
          ? 'La nota de entrega y «marcar como facturado» en MayorX no son la factura fiscal. La exportación automática de ventas al ERP está desactivada; amplíe los detalles para activarla.'
          : 'La nota de entrega y «marcar como facturado» en MayorX son pasos operativos internos; no sustituyen una factura fiscal en su ERP.';

  const toneClass = visualTone === 'embedded' ? styles.embedded : styles.soft;

  return (
    <div className={`${styles.wrap} ${toneClass} ${className}`.trim()}>
      <div className={styles.headRow}>
        <div className={styles.lead}>
          <span className={styles.iconBadge} aria-hidden>
            <FiReceipt size={18} />
          </span>
          <div className="min-w-0">
            <p className={styles.title}>{title}</p>
            <span className={styles.subtitle}>{subtitle}</span>
          </div>
        </div>
        <Button
          variant="link"
          className={styles.toggle}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Ocultar <FiChevronUp className="ms-1" aria-hidden />
            </>
          ) : (
            <>
              Detalles <FiChevronDown className="ms-1" aria-hidden />
            </>
          )}
        </Button>
      </div>

      <Collapse in={expanded}>
        <div className={styles.collapseInner}>
          {variant === 'integrations' ? (
            <ul className={styles.list}>
              <li>
                MayorX <strong>no sustituye</strong> al programa de facturación fiscal: aquí gestiona ventas,
                inventario y puede imprimir o enviar la <strong>nota de entrega</strong>.
              </li>
              <li>
                Con la integración activa, <strong>MayorX sincroniza cada venta aprobada</strong> hacia los{' '}
                <strong>pedidos</strong> de Profit Plus 2k8; por ahora ese es el vínculo previsto con el ERP. La{' '}
                <strong>factura fiscal</strong> la crea usted <strong>desde Profit Plus</strong>, a partir de ese pedido.
              </li>
              <li className="mb-0">
                Complete el asistente del puente (tablas de referencia y parámetros). Luego, en{' '}
                <Link to="/orders" className={styles.inlineLink}>
                  Ventas
                </Link>
                , encontrará la guía ampliable y el enlace a los pasos con imagen después de sincronizar un pedido.
              </li>
            </ul>
          ) : (
            <ul className={styles.list}>
              <li>
                <strong>Nota de entrega:</strong> documento para entregar mercancía o dejar constancia operativa.
                Puede imprimirla o enviarla por correo desde el detalle de la venta.{' '}
                <strong>No es una factura fiscal.</strong>
              </li>
              <li>
                <strong>Marcar como facturado:</strong> actualiza el estado de la venta en MayorX y, si tiene Profit
                Plus configurado, puede disparar la sincronización de inventario.{' '}
                <strong>No genera por sí solo una factura legal en el ERP.</strong>
              </li>
              {profitPlusAppReady && profitExportPedidosActive ? (
                <li className="mb-0">
                  <strong>Factura fiscal con Profit Plus 2k8:</strong> por el momento MayorX{' '}
                  <strong>sincroniza la venta aprobada como pedido</strong> en Profit Plus 2k8; desde ese pedido debe{' '}
                  <strong>crear la factura</strong> en Profit Plus (MayorX no la genera por sí solo). Después puede
                  usar <strong>Marcar como facturado</strong> en MayorX para alinear el seguimiento interno.
                  {onOpenProfitInvoiceHelp ? (
                    <>
                      {' '}
                      <Button
                        variant="link"
                        className={`${styles.inlineBtn} ${styles.inlineLink}`}
                        onClick={onOpenProfitInvoiceHelp}
                      >
                        Ver pasos con imagen
                      </Button>
                    </>
                  ) : null}
                </li>
              ) : profitPlusAppReady && !profitExportPedidosActive ? (
                <li className="mb-0">
                  <strong>Profit Plus está listo</strong> pero la exportación de ventas a pedidos no está activa.
                  Active «Ventas aprobadas como pedido en Profit» (y el modo de sincronización adecuado) en{' '}
                  <Link to="/configuration?tab=integrations" className={styles.inlineLink}>
                    Configuración → Integraciones → Profit Plus 2k8
                  </Link>
                  .
                </li>
              ) : (
                <li className="mb-0">
                  <strong>Factura fiscal:</strong> con Profit Plus 2k8, el flujo previsto es que MayorX envíe la venta
                  aprobada como <strong>pedido</strong> y que usted emita la factura desde Profit Plus; MayorX no crea
                  esa factura por sí solo. Active y complete la integración en{' '}
                  <Link to="/configuration?tab=integrations" className={styles.inlineLink}>
                    Configuración → Integraciones
                  </Link>
                  . Si usa otro ERP, registre la factura allí según su proceso habitual.
                </li>
              )}
            </ul>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default FacturacionGuideCard;
